<template>
  <AppDrawer
    :model-value="modelValue"
    :title="drawerTitle"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <template #eyebrow>
      <p class="font-mono text-2xs uppercase tracking-wide text-accent">
        {{ isTagSelector ? messages.drawer.eyebrow.tag : isGroupSelector ? messages.drawer.eyebrow.group : messages.drawer.eyebrow.board }}
      </p>
    </template>

    <div class="flex min-h-full flex-col gap-5 p-4">
      <PlayerFilterControls
        :filters="filters"
        :tags="filterableTags"
        :mode="mode"
        :show-mode="!isSelector"
        @update:filters="emit('update:filters', $event)"
        @update:mode="emit('update:mode', $event)"
      />

      <section
        v-if="!isSelector"
        class="rounded-lg border border-border bg-bg-surface px-4 py-3"
        aria-live="polite"
      >
        <p class="text-sm font-semibold text-text-primary">
          {{ messages.drawer.status.matchCount(currentBuildPlayers.length, store.currentPlayers.length) }}
        </p>
        <p v-if="!filtersActive" class="mt-1 text-xs text-text-muted">
          {{ messages.drawer.status.hintNoFilters }}
        </p>
        <p v-else-if="hasWeaponFilters" class="mt-1 text-xs text-text-muted">
          {{ messages.drawer.status.hintOtherBuilds }}
        </p>
        <p v-else-if="mode === PlayerFilterMode.Filter" class="mt-1 text-xs text-text-muted">
          {{ messages.drawer.status.hintFilterMode }}
        </p>
        <p v-else class="mt-1 text-xs text-text-muted">
          {{ messages.drawer.status.hintHighlightMode }}
        </p>
      </section>

      <section
        v-if="isSelector"
        class="border-t border-border pt-5"
        aria-labelledby="group-player-results-title"
      >
        <h3 id="group-player-results-title" class="text-sm font-semibold text-text-primary">
          {{ messages.drawer.selectorHeading(selectorTargetLabel) }}
        </h3>
        <p v-if="isTagSelector" class="text-xs text-text-muted">
          {{ messages.drawer.selectorHintTag }}
        </p>
        <p v-else class="text-xs text-text-muted">
          {{ messages.drawer.selectorHintGroup }}
        </p>
      </section>

      <div
        v-if="isSelector && selectorTarget === null"
        class="rounded-lg border border-danger/40 bg-bg-surface px-4 py-3 text-sm text-danger"
        role="alert"
      >
        {{ isTagSelector ? messages.drawer.selectorMissingTag : messages.drawer.selectorMissingGroup }}
      </div>

      <div
        v-else-if="hasWeaponFilters"
        class="flex min-h-0 flex-1 flex-col gap-5"
      >
        <section class="flex flex-col gap-3" aria-labelledby="current-build-results-title">
          <div class="flex flex-wrap items-end justify-between gap-2">
            <div>
              <h3 id="current-build-results-title" class="text-sm font-semibold text-text-primary">
                {{ messages.drawer.currentBuildHeading }}
              </h3>
              <p class="text-xs text-text-muted">
                {{ messages.drawer.currentBuildDescription }}
              </p>
            </div>
            <span class="rounded-full border border-border bg-bg-surface px-2.5 py-1 font-mono text-xs text-text-muted" aria-live="polite">
              {{ resultCountLabel(currentBuildPlayers.length) }}
            </span>
          </div>
          <PlayerFilterResultList
            :players="currentBuildPlayers"
            source="current"
            :list-label="isSelector ? messages.resultList.listLabelCurrentBuildSelector(selectorTargetLabel) : messages.resultList.listLabelCurrentBuildFilter"
            :empty-message="messages.resultList.emptyCurrentWeapon"
            :target-group-id="isGroupSelector ? targetGroupId : null"
            :target-group-label="targetGroupLabel"
            :target-tag-id="isTagSelector ? targetTagId : null"
            :target-tag-label="targetTagLabel"
            :pending-targets="pendingTargets"
            :pending-tag-player-ids="pendingTagPlayerIds"
            :weapon-filter-slugs="filters.weaponSlugs"
            @toggle-player="onTogglePlayer"
          />
        </section>

        <section class="flex flex-col gap-3 border-t border-border pt-5" aria-labelledby="saved-build-results-title">
          <div class="flex flex-wrap items-end justify-between gap-2">
            <div>
              <h3 id="saved-build-results-title" class="text-sm font-semibold text-text-primary">
                {{ messages.drawer.otherBuildsHeading }}
              </h3>
              <p class="text-xs text-text-muted">
                {{ messages.drawer.otherBuildsDescription }}
              </p>
            </div>
            <span class="rounded-full border border-border bg-bg-surface px-2.5 py-1 font-mono text-xs text-text-muted" aria-live="polite">
              {{ resultCountLabel(otherBuildPlayers.length) }}
            </span>
          </div>
          <PlayerFilterResultList
            :players="otherBuildPlayers"
            source="saved"
            :list-label="isSelector ? messages.resultList.listLabelOtherBuildSelector(selectorTargetLabel) : messages.resultList.listLabelOtherBuildFilter"
            :empty-message="messages.resultList.emptyOtherBuildWeapon"
            :target-group-id="isGroupSelector ? targetGroupId : null"
            :target-group-label="targetGroupLabel"
            :target-tag-id="isTagSelector ? targetTagId : null"
            :target-tag-label="targetTagLabel"
            :pending-targets="pendingTargets"
            :pending-tag-player-ids="pendingTagPlayerIds"
            :weapon-filter-slugs="filters.weaponSlugs"
            @toggle-player="onTogglePlayer"
          />
        </section>
      </div>

      <section
        v-else-if="isSelector"
        class="flex min-h-0 flex-1 flex-col gap-3"
        aria-labelledby="all-filter-results-title"
      >
        <div class="flex flex-wrap items-end justify-between gap-2">
          <h3 id="all-filter-results-title" class="text-sm font-semibold text-text-primary">
            {{ messages.drawer.matchingHeading }}
          </h3>
          <span class="rounded-full border border-border bg-bg-surface px-2.5 py-1 font-mono text-xs text-text-muted" aria-live="polite">
            {{ resultCountLabel(currentBuildPlayers.length) }}
          </span>
        </div>
        <PlayerFilterResultList
          :players="currentBuildPlayers"
          source="current"
          :list-label="messages.resultList.listLabelSelectorOnly(selectorTargetLabel)"
          :empty-message="messages.resultList.emptyNoFilters"
          :target-group-id="isGroupSelector ? targetGroupId : null"
          :target-group-label="targetGroupLabel"
          :target-tag-id="isTagSelector ? targetTagId : null"
          :target-tag-label="targetTagLabel"
          :pending-targets="pendingTargets"
          :pending-tag-player-ids="pendingTagPlayerIds"
          @toggle-player="onTogglePlayer"
        />
      </section>
    </div>
  </AppDrawer>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { tagDisplayName } from '@/features/gvg-planner/composables'
import { AppDrawer } from '@/shared/ui'
import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import {
  PlayerFilterMode,
  playerFiltersAreActive,
  playerMatchesFilters,
  playerMatchesNameAndTags,
} from '../../lib/playerFilters'
import type {
  PlayerFilterMode as PlayerFilterModeType,
  PlayerFilters,
} from '../../lib/playerFilters'
import { playerDisplayName } from '../../lib/playerIdentity'
import { playerSavedWeaponMatch } from '../../lib/playerWeaponBuilds'
import type { PlayerBuildChoice } from '../../lib/playerWeaponBuilds'
import { useGvgPlannerStore } from '../../stores/gvgPlannerStore'
import type { Player, PlayerGroup, PlayerTag } from '../../types'
import PlayerFilterControls from './PlayerFilterControls.vue'
import PlayerFilterResultList from './PlayerFilterResultList.vue'
import { playerFiltersEn } from './locales/en.ts'
import { playerFiltersRu } from './locales/ru.ts'

export interface PlayerFiltersDrawerProps {
  readonly modelValue: boolean
  readonly filters: PlayerFilters
  readonly mode: PlayerFilterModeType
  readonly targetGroupId?: string | null
  readonly targetTagId?: string | null
  readonly pendingTargets: ReadonlyMap<string, string>
  readonly pendingTagPlayerIds?: ReadonlySet<string>
}

const props = withDefaults(defineProps<PlayerFiltersDrawerProps>(), {
  targetGroupId: null,
  targetTagId: null,
  pendingTagPlayerIds: () => new Set<string>(),
})

const emit = defineEmits<{
  (event: 'update:modelValue', value: boolean): void
  (event: 'update:filters', filters: PlayerFilters): void
  (event: 'update:mode', mode: PlayerFilterModeType): void
  (
    event: 'toggle-player',
    playerId: string,
    selected: boolean,
    buildChoice: PlayerBuildChoice | null,
  ): void
  (event: 'toggle-tag-player', playerId: string, selected: boolean): void
}>()

const messages = useLocaleMessages(defineLocaleCatalog({ en: playerFiltersEn, ru: playerFiltersRu }))
const store = useGvgPlannerStore()
const isGroupSelector = computed<boolean>(() => props.targetGroupId !== null)
const isTagSelector = computed<boolean>(() => props.targetTagId !== null)
const isSelector = computed<boolean>(() => isGroupSelector.value || isTagSelector.value)
const filtersActive = computed<boolean>(() => playerFiltersAreActive(props.filters))
const hasWeaponFilters = computed<boolean>(() => props.filters.weaponSlugs.length > 0)
const targetGroup = computed<PlayerGroup | null>(() => {
  if (props.targetGroupId === null) return null
  return store.currentGroups.find((group) => group.id === props.targetGroupId) ?? null
})
const targetGroupLabel = computed<string>(() => {
  const group = targetGroup.value
  return group === null ? messages.value.resultList.fallbackGroupLabelProp : displayGroupLabel(group)
})
const targetTag = computed<PlayerTag | null>(() => {
  if (props.targetTagId === null) return null
  return store.playerTags.find((tag) => tag.id === props.targetTagId) ?? null
})
const targetTagLabel = computed<string>(() => {
  const tag = targetTag.value
  return tag === null ? messages.value.resultList.fallbackTagLabelProp : tagDisplayName(tag.name)
})
const selectorTarget = computed<PlayerGroup | PlayerTag | null>(() =>
  isTagSelector.value ? targetTag.value : targetGroup.value,
)
const selectorTargetLabel = computed<string>(() =>
  isTagSelector.value ? targetTagLabel.value : targetGroupLabel.value,
)
const filterableTags = computed<ReadonlyArray<PlayerTag>>(() =>
  props.targetTagId === null
    ? store.playerTags
    : store.playerTags.filter((tag) => tag.id !== props.targetTagId),
)
const drawerTitle = computed<string>(() =>
  isTagSelector.value
    ? messages.value.drawer.titleForTag(targetTagLabel.value)
    : isGroupSelector.value
      ? messages.value.drawer.titleForGroup(targetGroupLabel.value)
      : messages.value.drawer.titleFallback,
)
const currentBuildPlayers = computed<ReadonlyArray<Player>>(() =>
  sortPlayers(store.currentPlayers.filter((player) => playerMatchesFilters(player, props.filters))),
)
const otherBuildPlayers = computed<ReadonlyArray<Player>>(() => {
  if (!hasWeaponFilters.value) return []
  return sortPlayers(store.currentPlayers.filter((player) => {
    if (
      playerMatchesFilters(player, props.filters)
      || !playerMatchesNameAndTags(player, props.filters)
    ) {
      return false
    }
    const match = playerSavedWeaponMatch(player, props.filters.weaponSlugs)
    return match.personal || match.guild
  }))
})

function resultCountLabel(count: number): string {
  return count === 1
    ? messages.value.drawer.resultCountSingular(count)
    : messages.value.drawer.resultCountPlural(count)
}

function sortPlayers(players: ReadonlyArray<Player>): ReadonlyArray<Player> {
  const result = players.slice()
  result.sort((left, right) =>
    playerDisplayName(left, store.playerNameMode).localeCompare(
      playerDisplayName(right, store.playerNameMode),
      undefined,
      { sensitivity: 'base' },
    ),
  )
  return result
}

function displayGroupLabel(group: PlayerGroup): string {
  if (group.label !== '') return group.label
  const room = store.currentRooms.find((candidate) => candidate.groupIds.includes(group.id))
  const index = room?.groupIds.indexOf(group.id) ?? -1
  return index < 0 ? group.id : messages.value.resultList.fallbackGroupLabel(index + 1)
}

function onTogglePlayer(
  playerId: string,
  selected: boolean,
  buildChoice: PlayerBuildChoice | null,
): void {
  if (isTagSelector.value) {
    emit('toggle-tag-player', playerId, selected)
    return
  }
  emit('toggle-player', playerId, selected, buildChoice)
}
</script>
